import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  createMatterPropertyDamageV3,
  deleteMatterPropertyDamageV1,
  updateMatterPropertyDamageV1,
} from "@/lib/data/requests/matters/matter-property-damage";
import { queryKeys } from "@/lib/data/static/query-keys";
import {
  type APIError,
  type APIResponse,
  type Matter,
  type MatterPropertyDamage,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import dayjs from "dayjs";
import toast from "react-hot-toast";

const withPropertyDamages = (
  matter: Matter,
  propertyDamages: MatterPropertyDamage[],
): Matter => ({
  ...matter,
  propertyDamages,
});

export const useCreateMatterPropertyDamage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterPropertyDamageV3>[0]) =>
      createMatterPropertyDamageV3(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: withPropertyDamages(oldData.data, [
              ...oldData.data.propertyDamages,
              {
                ...data,
                dateCreated: dayjs().utc().format("YYYY-MM-DDTHH:mm:ss"),
                id: "optimistic",
              } as MatterPropertyDamage,
            ]),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toast.error("Error creating property damage");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      toast.success("Property damage created successfully");
    },
  });
};

export const useUpdateMatterPropertyDamage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterPropertyDamageV1>[0]) =>
      updateMatterPropertyDamageV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: withPropertyDamages(
              oldData.data,
              oldData.data.propertyDamages.map((propertyDamage) =>
                propertyDamage.id === data.id
                  ? { ...propertyDamage, ...data }
                  : propertyDamage,
              ),
            ),
          };
        },
      );
      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating property damage");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      toast.success("Property damage updated successfully");
    },
  });
};

export const useDeleteMatterPropertyDamage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterPropertyDamageV1>[0]) =>
      deleteMatterPropertyDamageV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: withPropertyDamages(
              oldData.data,
              oldData.data.propertyDamages.filter(
                (propertyDamage) => propertyDamage.id !== data.id,
              ),
            ),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting property damage");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      toast.success("Property damage deleted successfully");
    },
  });
};
